import NewsquawkHeadlineItem from "./NewsquawkHeadlineItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommonWidgetProps } from "../types";
import { HighlightInfo } from "@/utils/portfolioUtils";
import { Headline } from "./models/Headline";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import EditHeadlineFormDialog from "./EditHeadlineFormDialog";
import PollingModeIndicator from "./PollingModeIndicator";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { useAccessControl } from "@/hooks/useAccessControl";
import { PricingModal } from "@/ui-components/PricingModal";
import { ClockIcon } from "@heroicons/react/24/outline";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { usePortfolioBuilderOptional } from "@/pages/PortfolioBuilder/components/PortfolioBuilderContext";
import { applyFilters } from "./hooks/useHeadlinesHighlightFilterMenu";
import { useHeadlinesFilterPanel } from "./hooks/useHeadlinesFilterPanel";
import { useHeadlinesHighlightPanel } from "./hooks/useHeadlinesHighlightPanel";
import { FilterPanel } from "@/ui-components/FilterPanel";
import useStableArray from "@/hooks/useStableArray";
import { useHeadlineQueriesCRUD } from "./api/headline-query/useHeadlineQueriesCRUD";
import { CategoryQuery } from "./models/CategoryQuery";
import {
  mapPortfolioIdsToPortfolios,
} from "../firehose-widgets/shared/hooks/searchUtils";
import {
  useHeadlines,
  useHeadlinesCRUD,
  useDelayedHeadlines,
  useSearchHeadlines,
  useIsPollingMode,
} from "@/shared/api/newsquawk-headlines";
import { useHeadlinesFilterStore } from "@/shared/api/newsquawk-headlines/HeadlinesFilterStore";
import { trackFilterApplied } from "@/utils/gtm";
import { menuCategories } from "./models/categories";
import {
  VirtuosoWrapper,
  GroupingUtils,
} from "@/ui-components/grouped-virtuoso";
import { DateSectionHeader } from "../firehose-widgets/shared/DateSectionHeader";

/** Resolve tag IDs to category names using the static menuCategories list */
const allCategoryItems = menuCategories.flatMap((group) => group.items);
function resolveTagNames(tagIds: (string | number)[]): string {
  return tagIds
    .map((id) => allCategoryItems.find((item) => item.id === Number(id))?.title)
    .filter(Boolean)
    .join(", ");
}

function NewsquawkHeadlines(props: CommonWidgetProps) {
  const { hasLiveAccess } = useAccessControl();
  const isPollingMode = useIsPollingMode();
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  const {
    isLoading: headlinesLoading,
    headlines,
    loadNextPage,
    hasMore: hasMoreHeadlines,
  } = useHeadlines();

  const { deleteMutation } = useHeadlinesCRUD();

  // For users without live access (unauthenticated or non-subscriber), use delayed headlines
  const {
    headlines: delayedHeadlines,
    hasMore: delayedHasMore,
    loadNextPage: loadNextDelayedPage,
    isLoading: delayedIsLoading,
    loadingNextPage: delayedLoadingNextPage,
  } = useDelayedHeadlines({ subscribe: !hasLiveAccess });

  const {
    searchHeadlines,
    searchResults,
    clearSearchResults,
    isLoading: searchLoading,
    loadNext: loadNextSearchPage,
    hasMore: hasMoreSearchResults,
    isLoadingMore: isLoadingMoreSearchResults,
  } = useSearchHeadlines();

  // Keep track of the item being edited or deleted,
  // so that the relevant dialog can be shown
  const [editingItem, setEditingItem] = useState<Headline | null>(null);
  const [deletingItem, setDeletingItem] = useState<Headline | null>(null);


  const { portfolios } = usePortfoliosCRUD();

  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();

  // Get preview state from PortfolioBuilder context (if available)
  const portfolioBuilderContext = usePortfolioBuilderOptional();

  // Track which panel was last opened to coordinate closing
  const [lastOpenedPanel, setLastOpenedPanel] = useState<
    "filter" | "highlight" | null
  >(null);

  // Memoize onOpen callbacks for panel coordination
  const handleHighlightPanelOpen = useCallback(() => {
    setLastOpenedPanel("highlight");
  }, []);

  const handleFilterPanelOpen = useCallback(() => {
    setLastOpenedPanel("filter");
  }, []);

  // Highlight panel
  const {
    selections: highlightSelections,
    setSelections: setHighlightSelections,
    isPanelOpen: isHighlightPanelOpen,
    closePanel: closeHighlightPanel,
    filterMenus: highlightMenus,
    clearSelections: clearHighlightSelections,
  } = useHeadlinesHighlightPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleHighlightPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Filter panel
  const {
    selections: filterSelections,
    setSelections: setFilterSelections,
    isPanelOpen: isFilterPanelOpen,
    closePanel: closeFilterPanel,
    filterMenus,
    clearSelections: clearFilterSelections,
    filterMode,
    tagWatchlists,
  } = useHeadlinesFilterPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleFilterPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Panel coordination: when one opens, close the other
  useEffect(() => {
    if (lastOpenedPanel === "filter" && isHighlightPanelOpen) {
      closeHighlightPanel();
    } else if (lastOpenedPanel === "highlight" && isFilterPanelOpen) {
      closeFilterPanel();
    }
  }, [
    lastOpenedPanel,
    isFilterPanelOpen,
    isHighlightPanelOpen,
    closeFilterPanel,
    closeHighlightPanel,
  ]);

  // Combined selections for applyFilters (needs both filter and highlight)
  const combinedSelections = useMemo(
    () => ({
      ...filterSelections,
      ...highlightSelections,
    }),
    [filterSelections, highlightSelections]
  );

  // Sync preview state from PortfolioBuilder to filter selections
  useEffect(() => {
    if (!portfolioBuilderContext) return;

    const { previewState } = portfolioBuilderContext;

    if (previewState.type === "watchlist" && previewState.id !== null) {
      // Preview a watchlist - set ticker-watchlists-filter (portfolios are ticker-based by default)
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [String(previewState.id)],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "filter" && previewState.id !== null) {
      // Preview a custom filter - set category-filter
      setFilterSelections((prev) => ({
        ...prev,
        "category-filter": [String(previewState.id)],
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "tagWatchlist" && previewState.tagIds) {
      // Preview a tag watchlist - set basic-filter with the tag IDs
      const tagIds = previewState.tagIds;
      setFilterSelections((prev) => ({
        ...prev,
        "basic-filter": tagIds,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else {
      // No preview - clear all preview-related filters
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    }
  }, [portfolioBuilderContext?.previewState, setFilterSelections]);

  // Get global filter store actions for syncing portfolio selections (for sound alerts)
  const setPortfoliosFilter = useHeadlinesFilterStore(
    (state) => state.setPortfoliosFilter
  );
  const setPortfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.setPortfoliosHighlight
  );

  const basicFilterSelected = useStableArray(
    filterSelections["basic-filter"] || []
  );

  // Get selected tag watchlists and extract their tag IDs
  const tagWatchlistsFilterSelected = useStableArray(
    filterSelections["tag-watchlists-filter"] || []
  );

  // Extract tag IDs from selected tag watchlists
  const tagWatchlistTagIds = useMemo(() => {
    if (tagWatchlistsFilterSelected.length === 0) return [];
    return tagWatchlistsFilterSelected.flatMap((watchlistId) => {
      const watchlist = tagWatchlists.find((w) => String(w.id) === watchlistId);
      return watchlist?.tags || [];
    });
  }, [tagWatchlistsFilterSelected, tagWatchlists]);

  // Get selected queries to search for
  const queriesToSearch = useStableArray(
    filterSelections["category-filter"]
      ?.map((id) => {
        if (categoryQueries.data) {
          return categoryQueries.data.find((query) => String(query.id) === id);
        }
        return undefined;
      })
      .filter(Boolean) || []
  ) as CategoryQuery[];

  // Extract tag IDs from advanced filters' embedded tag watchlists
  const advancedFilterTagIds = useMemo(() => {
    if (queriesToSearch.length === 0) return [];
    return queriesToSearch.flatMap((query) =>
      (query.tagsWatchlists || []).flatMap((tw) => tw.tags)
    );
  }, [queriesToSearch]);

  // Get selected portfolios from ticker and keyword watchlist filters
  const portfoliosFilterSelected = useStableArray([
    ...(filterSelections["ticker-watchlists-filter"] || []),
    ...(filterSelections["keyword-watchlists-filter"] || []),
  ]);

  // Sync portfolio filter/highlight selections to global store for sound alerts.
  // Includes portfolios embedded in active advanced filters (CategoryQueries)
  // so a portfolio with sound enabled fires even when it's only referenced via
  // a CategoryQuery rather than a standalone watchlist selection.
  useEffect(() => {
    const advancedFilterPortfolioIds = queriesToSearch.flatMap(
      (q) => (q.portfolios || []).map((p) => p.id)
    );

    const highlightPortfolioIds = [
      ...new Set(
        [
          ...(highlightSelections["ticker-watchlists-highlight"] ?? []),
          ...(highlightSelections["keyword-watchlists-highlight"] ?? []),
        ].map((id) => Number(id))
      ),
    ];
    const filterPortfolioIds = [
      ...new Set([
        ...[
          ...(filterSelections["ticker-watchlists-filter"] ?? []),
          ...(filterSelections["keyword-watchlists-filter"] ?? []),
        ].map((id) => Number(id)),
        ...advancedFilterPortfolioIds,
      ]),
    ];

    setPortfoliosHighlight(highlightPortfolioIds);
    setPortfoliosFilter(filterPortfolioIds);

    // Cleanup: clear selections when widget unmounts
    return () => {
      setPortfoliosHighlight([]);
      setPortfoliosFilter([]);
    };
  }, [
    filterSelections,
    highlightSelections,
    queriesToSearch,
    setPortfoliosFilter,
    setPortfoliosHighlight,
  ]);

  const hasFilters =
    queriesToSearch.length > 0 ||
    basicFilterSelected.length > 0 ||
    portfoliosFilterSelected.length > 0 ||
    tagWatchlistTagIds.length > 0;

  const showSearchResults = hasFilters || searchLoading;

  // Determine which data source to use based on subscription status
  // Non-subscribers and unauthenticated users get delayed headlines, subscribers get live headlines
  const baseHeadlines = hasLiveAccess ? headlines : delayedHeadlines;
  const baseLoadNextPage = hasLiveAccess ? loadNextPage : loadNextDelayedPage;
  const baseHasMore = hasLiveAccess ? hasMoreHeadlines : delayedHasMore;
  const baseIsLoading = hasLiveAccess ? headlinesLoading : delayedIsLoading;
  const baseIsLoadingMore = hasLiveAccess
    ? headlinesLoading
    : delayedLoadingNextPage;

  // Determine which pagination/loading to use based on search state
  const loadNext = showSearchResults ? loadNextSearchPage : baseLoadNextPage;
  const hasMore = showSearchResults ? hasMoreSearchResults : baseHasMore;

  const isLoadingMore = showSearchResults
    ? isLoadingMoreSearchResults
    : baseIsLoadingMore;

  /* Show search results if there are any, else show recent headlines */
  // Filter headlines based on the portfolios to filter
  const filteredHeadlines = showSearchResults ? searchResults : baseHeadlines;

  const filteredData = applyFilters(
    filteredHeadlines,
    combinedSelections,
    portfolios
  );


  const handleQueriesApplied = useCallback(
    (queryIds: number[]) => {
      console.log("Queries applied: ", queryIds);

      setFilterSelections((prev) => ({
        ...prev,
        "category-filter": queryIds.map((id) => String(id)),
      }));
    },
    [setFilterSelections]
  );

  // Push polling mode indicator into widget header when in polling fallback
  useEffect(() => {
    if (isPollingMode && hasLiveAccess) {
      props.headerOptions?.pushAction(
        <PollingModeIndicator key="00-polling-mode" />
      );
    } else {
      props.headerOptions?.setActions((prev) =>
        prev.filter((a) => a.key !== "00-polling-mode")
      );
    }
  }, [isPollingMode, hasLiveAccess, props.headerOptions]);

  /**
   * Performs a search for headlines and updates the widget's title
   * whenever the selected filters change.
   *
   * The effect:
   * 1. Determines if any filters are active.
   * 2. If filters are active, constructs search parameters (tags, keywords, tickers)
   *    based on the selected filters and performs a search using `searchHeadlines`.
   * 3. Dynamically builds and sets the widget title to reflect the active filters.
   * 4. If no filters are active, resets the widget title to the default and clears search results.
   *
   */
  useEffect(() => {
    const hasAdvancedFilters = queriesToSearch.length > 0;

    const hasFilters =
      hasAdvancedFilters ||
      basicFilterSelected.length > 0 ||
      portfoliosFilterSelected.length > 0 ||
      tagWatchlistTagIds.length > 0;

    if (hasFilters) {
      const portfolioFilterIds = portfoliosFilterSelected.map((id) =>
        String(id)
      );

      const portfoliosToSearch = mapPortfolioIdsToPortfolios(
        portfolios,
        portfolioFilterIds
      );

      // Combine basic filter tags, tag watchlist tags, and advanced filter tag watchlist tags (deduped)
      const allTagIds = [
        ...new Set([
          ...basicFilterSelected.map((id) => Number(id)),
          ...tagWatchlistTagIds.map((id) => Number(id)),
          ...advancedFilterTagIds.map((id) => Number(id)),
        ]),
      ];

      searchHeadlines({
        subscribed: true,
        exactMatch: false,
        rows: 50,
        portfolios: portfoliosToSearch,
        advancedFilter: queriesToSearch[0],
        tags: allTagIds,
      });

      // Build title based on active filters
      const titleParts: string[] = [];

      if (queriesToSearch.length > 0) {
        const filterNames = queriesToSearch.map((f) => f.queryName).filter(Boolean).join(", ");
        if (filterNames) titleParts.push(filterNames);
      }
      if (portfoliosToSearch.length > 0) {
        const portfolioNames = portfoliosToSearch.map((p) => p.name).filter(Boolean).join(", ");
        if (portfolioNames) titleParts.push(portfolioNames);
      }
      if (basicFilterSelected.length > 0) {
        const assetClassNames = resolveTagNames(basicFilterSelected);
        if (assetClassNames) titleParts.push(assetClassNames);
      }

      if (titleParts.length > 0) {
        props.headerOptions?.setTitle(`Newsquawk Headlines (Filter: ${titleParts.join(" + ")})`);
      } else {
        props.headerOptions?.setTitle("Showing filtered results");
      }
    } else {
      props.headerOptions?.setTitle("Newsquawk Headlines");
      clearSearchResults();
    }
  }, [
    queriesToSearch,
    basicFilterSelected,
    portfoliosFilterSelected,
    tagWatchlistTagIds,
    advancedFilterTagIds,
    props.headerOptions,
    clearSearchResults,
    searchHeadlines,
    portfolios,
    filterMode,
  ]);

  // ── Track filter usage (debounced) ──────────────────────────────────
  const filterTrackingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    filterTrackingTimer.current = setTimeout(() => {
      if (basicFilterSelected.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "category",
          filter_value: resolveTagNames(basicFilterSelected) || basicFilterSelected.join(", "),
          filter_count: basicFilterSelected.length,
        });
      }
      if (portfoliosFilterSelected.length > 0) {
        const portfolioNames = portfoliosFilterSelected
          .map((id) => portfolios?.find((p) => String(p.id) === String(id))?.name)
          .filter(Boolean)
          .join(", ");
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "portfolio",
          filter_value: portfolioNames || portfoliosFilterSelected.join(", "),
          filter_count: portfoliosFilterSelected.length,
        });
      }
      if (tagWatchlistTagIds.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "tag_watchlist",
          filter_value: resolveTagNames(tagWatchlistTagIds) || "tag_watchlist",
          filter_count: tagWatchlistTagIds.length,
        });
      }
      if (queriesToSearch.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "advanced_filter",
          filter_value: queriesToSearch.map((q) => q.queryName).join(", "),
          filter_count: queriesToSearch.length,
        });
      }
    }, 500);
    return () => {
      if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    };
  }, [basicFilterSelected, portfoliosFilterSelected, tagWatchlistTagIds, queriesToSearch, portfolios]);

  return (
    <>
      <PricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
      />
      <div className="h-full flex flex-col relative">
        {/* Filter Panel - inline above content */}
        <FilterPanel
          filterMenus={filterMenus}
          selections={filterSelections}
          onChange={setFilterSelections}
          isOpen={isFilterPanelOpen}
          onClearSelections={clearFilterSelections}
          onClose={closeFilterPanel}
        />

        {/* Highlight Panel - inline above content */}
        <FilterPanel
          filterMenus={highlightMenus}
          selections={highlightSelections}
          onChange={setHighlightSelections}
          isOpen={isHighlightPanelOpen}
          onClearSelections={clearHighlightSelections}
          onClose={closeHighlightPanel}
          variant="highlight"
        />

        {/** Headlines list */}
        <VirtuosoWrapper
          data={filteredData}
          grouping={{
            groupBy: (items) =>
              GroupingUtils.groupByDate(items, (item) => {
                const h = item[0];
                return typeof h.published_at === "object"
                  ? new Date(h.published_at.unix * 1000).toISOString()
                  : (h.published_at as string);
              }, {
                includeYesterday: true,
                dateFormat: { weekday: "short", month: "long", day: "numeric", year: "numeric" },
              }),
            hideFirstGroupHeader: !showSearchResults,
          }}
          scrollManager={{
            enabled: true,
            notificationText: "New headlines available",
            compareFirstItem: (a, b) => a?.[0]?.id === b?.[0]?.id,
          }}
          pagination={{
            hasMore,
            isLoadingMore,
            loadNext,
          }}
          renderItem={([headline, matchedPortfolio]) => {
            const highlightInfo: HighlightInfo | undefined = matchedPortfolio
              ? {
                  portfolioId: matchedPortfolio.id,
                  portfolioName: matchedPortfolio.name,
                  color: matchedPortfolio.color,
                  matchedTickers: [
                    ...(matchedPortfolio.tickers || []),
                    ...(matchedPortfolio.keywords || []),
                  ],
                }
              : undefined;

            return (
              <NewsquawkHeadlineItem
                headline={headline}
                key={headline.id}
                highlightInfo={highlightInfo || undefined}
                onItemDelete={setDeletingItem}
                onItemEdit={setEditingItem}
              />
            );
          }}
          renderGroupHeader={(title) => <DateSectionHeader title={title} />}
          renderEmpty={() => (
            <EmptyPlaceholder
              isLoading={showSearchResults ? searchLoading : baseIsLoading}
              delay={200}
            />
          )}
          className="flex-1 scrollbar-thin bg-white dark:bg-gray-900 relative"
          components={{
            Header: () =>
              !hasLiveAccess && (
                <div className="flex items-center justify-center gap-3 bg-gray-100/90 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 py-2 px-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <ClockIcon className="h-3.5 w-3.5 text-terminal-orange" />
                    <span>
                      <span className="text-terminal-orange font-medium">
                        24h delayed
                      </span>
                    </span>
                  </div>
                  <button
                    onClick={() => setIsPricingOpen(true)}
                    className="px-2.5 py-0.5 text-xs font-medium text-white bg-squawk-blue hover:bg-squawk-blue/80 rounded-md transition-colors cursor-pointer"
                  >
                    Get Live Data
                  </button>
                </div>
              ),
          }}
        />
      </div>

      {/* Dialogs */}
      <section>
        <DeleteConfirmationDialog
          isOpen={deletingItem !== null}
          onClose={() => setDeletingItem(null)}
          onConfirmDelete={
            deletingItem
              ? () => deleteMutation.mutateAsync(deletingItem.id)
              : undefined
          }
        />

        <EditHeadlineFormDialog
          isOpen={editingItem !== null}
          headline={editingItem}
          onClose={() => setEditingItem(null)}
        />
      </section>
    </>
  );
}

export default NewsquawkHeadlines;
